import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

const ThemeContext = createContext();

// Color presets available for sections
export const COLOR_PRESETS = {
    blue:    { name: 'Azul',      primary: '#0ea5e9', hover: '#0284c7', light: '#e0f2fe', lighter: '#f0f9ff', dark: '#0369a1', ring: '#7dd3fc', text: '#0c4a6e' },
    sky:     { name: 'Celeste',   primary: '#38bdf8', hover: '#0ea5e9', light: '#e0f2fe', lighter: '#f0f9ff', dark: '#0284c7', ring: '#7dd3fc', text: '#075985' },
    indigo:  { name: 'Índigo',    primary: '#6366f1', hover: '#4f46e5', light: '#e0e7ff', lighter: '#eef2ff', dark: '#4338ca', ring: '#a5b4fc', text: '#3730a3' },
    violet:  { name: 'Violeta',   primary: '#8b5cf6', hover: '#7c3aed', light: '#ede9fe', lighter: '#f5f3ff', dark: '#6d28d9', ring: '#c4b5fd', text: '#5b21b6' },
    emerald: { name: 'Esmeralda', primary: '#10b981', hover: '#059669', light: '#d1fae5', lighter: '#ecfdf5', dark: '#047857', ring: '#6ee7b7', text: '#064e3b' },
    rose:    { name: 'Rosa',      primary: '#f43f5e', hover: '#e11d48', light: '#ffe4e6', lighter: '#fff1f2', dark: '#be123c', ring: '#fda4af', text: '#881337' },
    amber:   { name: 'Ámbar',     primary: '#f59e0b', hover: '#d97706', light: '#fef3c7', lighter: '#fffbeb', dark: '#b45309', ring: '#fcd34d', text: '#78350f' },
    teal:    { name: 'Teal',      primary: '#14b8a6', hover: '#0d9488', light: '#ccfbf1', lighter: '#f0fdfa', dark: '#0f766e', ring: '#5eead4', text: '#134e4a' },
    cyan:    { name: 'Cian',      primary: '#06b6d4', hover: '#0891b2', light: '#cffafe', lighter: '#ecfeff', dark: '#0e7490', ring: '#67e8f9', text: '#155e75' },
    slate:   { name: 'Gris',      primary: '#64748b', hover: '#475569', light: '#e2e8f0', lighter: '#f1f5f9', dark: '#334155', ring: '#94a3b8', text: '#1e293b' },
};

// Section keys
export const SECTIONS = {
    generador: { label: 'Generador', icon: '📝' },
    tareas:    { label: 'Tareas',    icon: '✅' },
    calendario:{ label: 'Calendario',icon: '📅' },
    horasExtra:{ label: 'Horas Extra',icon: '⏱️' },
    ventas:    { label: 'Ventas',    icon: '💰' },
    chat:      { label: 'Chat',      icon: '💬' },
};

const DEFAULT_SECTION_COLORS = {
    generador:  'blue',
    tareas:     'blue',
    calendario: 'blue',
    horasExtra: 'blue',
    ventas:     'blue',
    chat:       'blue',
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) return savedTheme;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    const [sectionColors, setSectionColors] = useState(() => {
        try {
            const saved = localStorage.getItem('sectionColors');
            return saved ? { ...DEFAULT_SECTION_COLORS, ...JSON.parse(saved) } : DEFAULT_SECTION_COLORS;
        } catch { return DEFAULT_SECTION_COLORS; }
    });

    const [applyGlobal, setApplyGlobal] = useState(() => {
        return localStorage.getItem('applyGlobalTheme') === 'true';
    });

    const [globalColor, setGlobalColor] = useState(() => {
        return localStorage.getItem('globalColor') || 'blue';
    });

    // Listen to Firestore for shared theme config
    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'app_config', 'theme'), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (data.sectionColors) {
                    setSectionColors(prev => ({ ...prev, ...data.sectionColors }));
                    localStorage.setItem('sectionColors', JSON.stringify({ ...DEFAULT_SECTION_COLORS, ...data.sectionColors }));
                }
                if (data.applyGlobal !== undefined) {
                    setApplyGlobal(data.applyGlobal);
                    localStorage.setItem('applyGlobalTheme', String(data.applyGlobal));
                }
                if (data.globalColor) {
                    setGlobalColor(data.globalColor);
                    localStorage.setItem('globalColor', data.globalColor);
                }
            }
        }, () => {});
        return () => unsub();
    }, []);

    useEffect(() => {
        const root = window.document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = (newTheme) => {
        setTheme(newTheme);
    };

    // Get the color preset for a given section
    const getColors = useCallback((section) => {
        const colorKey = applyGlobal ? globalColor : (sectionColors[section] || 'blue');
        return COLOR_PRESETS[colorKey] || COLOR_PRESETS.blue;
    }, [applyGlobal, globalColor, sectionColors]);

    // Save theme config to Firestore (admin only)
    const saveThemeConfig = useCallback(async (config) => {
        try {
            await setDoc(doc(db, 'app_config', 'theme'), config, { merge: true });
        } catch (err) {
            console.error('Error saving theme config:', err);
        }
    }, []);

    const updateSectionColor = useCallback((section, colorKey) => {
        const updated = { ...sectionColors, [section]: colorKey };
        setSectionColors(updated);
        localStorage.setItem('sectionColors', JSON.stringify(updated));
        saveThemeConfig({ sectionColors: updated });
    }, [sectionColors, saveThemeConfig]);

    const updateGlobalSettings = useCallback((isGlobal, color) => {
        setApplyGlobal(isGlobal);
        setGlobalColor(color);
        localStorage.setItem('applyGlobalTheme', String(isGlobal));
        localStorage.setItem('globalColor', color);
        saveThemeConfig({ applyGlobal: isGlobal, globalColor: color });
    }, [saveThemeConfig]);

    return (
        <ThemeContext.Provider value={{
            theme, toggleTheme,
            sectionColors, updateSectionColor,
            applyGlobal, globalColor, updateGlobalSettings,
            getColors, COLOR_PRESETS, SECTIONS,
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
